import { getRouteApi } from '@tanstack/react-router'
import { useTranslations, useLocale } from 'use-intl'
import { useShopDetail } from '../hooks/use-shop-detail'
import { ShopHeroGallery } from './ShopHeroGallery'
import { ShopAwardsBadge } from './ShopAwardsBadge'
import { ShopHeader } from './ShopHeader'
import { ShopStory } from './ShopStory'
import { ShopScoreBreakdown } from './ShopScoreBreakdown'
import { ShopInfoSection } from './ShopInfoSection'
import { ShopMenuSection } from './ShopMenuSection'
import { ShopHighlights } from './ShopHighlights'
import { ShopActionBar } from './ShopActionBar'
import { computePriceLevel } from '~/features/search/lib/map-shop-to-card-props'
import { ArrowLeft, ChevronRight, Home } from 'lucide-react'
import { Button } from '@heroui/react'
import { LocalizedLink } from '~/components/common/localized-link'
import { getCleanShopName } from '~/utils/shop-name'

const route = getRouteApi('/{-$locale}/_public/shops/$shopId')

export function ShopDetailPage() {
  const params = route.useParams()
  const shopId = (params as any).shopId
  const locale = useLocale()
  const { data: shop, isLoading, isError } = useShopDetail(shopId, locale)
  const t = useTranslations('shopDetail')
  const tDiscovery = useTranslations('discovery')

  if (isLoading) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    )
  }

  if (isError || !shop) {
    return (
      <div className="min-h-screen bg-surface flex flex-col items-center justify-center p-6 text-center">
        <h1 className="text-2xl font-bold mb-2 text-on-surface">{t('notFound')}</h1>
        <p className="text-on-surface-variant mb-6">Could not find information for this restaurant.</p>
        <button 
          onClick={() => window.history.back()}
          className="px-6 py-2 bg-surface-container-high rounded-full font-medium"
        >
          {t('backToMap')}
        </button>
      </div>
    )
  }

  // Parse location — DB stores as [lat, lng] array
  let locationData = null
  if (shop.location) {
    if (Array.isArray(shop.location)) {
      // location[0] = latitude (y), location[1] = longitude (x)
      locationData = { x: shop.location[1], y: shop.location[0] }
    } else if ((shop.location as any).x !== undefined) {
      locationData = { x: (shop.location as any).x, y: (shop.location as any).y }
    }
  }

  const budgetMin = shop.dinnerBudgetMin || shop.lunchBudgetMin || null
  const budgetMax = shop.dinnerBudgetMax || shop.lunchBudgetMax || null
  const priceLevel = computePriceLevel(budgetMin, budgetMax)

  // Determine if there's enough left-column content to justify a two-column layout
  const hasRichContent = !!(
    shop.translation?.description ||
    (shop.translation?.menus as any)?.length ||
    (shop.translation?.highlights as any)?.length
  )

  const cleanName = getCleanShopName(shop.translation?.name, shop.translation?.alias)

  const headerBlock = (
    <ShopHeader 
      name={cleanName}
      originalName={shop.translation?.alias || null}
      rating={shop.ratingScore !== null ? Number(shop.ratingScore) : null}
      category={shop.translation?.category || ''}
      priceLevel={priceLevel}
    />
  )

  const scoreBlock = (
    <ShopScoreBreakdown 
      tasteScore={shop.tasteScore}
      serviceScore={shop.serviceScore}
      atmosphereScore={shop.atmosphereScore}
      drinksScore={shop.drinksScore}
      cpScore={shop.cpScore}
    />
  )

  const infoBlock = (
    <ShopInfoSection 
      address={shop.translation?.address || null}
      access={shop.translation?.access || null}
      openHours={shop.translation?.openHours || null}
      lunchBudgetMin={shop.lunchBudgetMin}
      lunchBudgetMax={shop.lunchBudgetMax}
      dinnerBudgetMin={shop.dinnerBudgetMin}
      dinnerBudgetMax={shop.dinnerBudgetMax}
      budgetString={shop.translation?.budget ? (shop.translation.budget as any).text || null : null}
      capacity={shop.capacity}
      hasPrivateRoom={shop.hasPrivateRoom}
      hasParking={shop.hasParking}
      kidsAllowed={shop.kidsAllowed}
      smokingStatus={shop.smokingStatus}
      hasOnlineBooking={shop.hasOnlineBooking}
      acceptsCreditCard={shop.acceptsCreditCard}
      acceptsElectronicMoney={shop.acceptsElectronicMoney}
      acceptsQrCode={shop.acceptsQrCode}
    />
  )

  const actionBlock = (
    <ShopActionBar 
      location={locationData}
      tabelogUrl={shop.translation?.url || null}
      rawName={shop.translation?.name || null}
      cleanName={cleanName}
      aliasName={shop.translation?.alias || null}
      address={shop.translation?.address || null}
    />
  )

  const breadcrumbBlock = (
    <nav aria-label="Breadcrumb" className="flex items-center text-sm text-stone-500 dark:text-stone-400 font-medium overflow-x-auto no-scrollbar whitespace-nowrap">
      <LocalizedLink to="/" className="flex-none flex items-center hover:text-stone-900 dark:hover:text-stone-100 transition-colors">
        <Home className="w-4 h-4" />
      </LocalizedLink>
      <ChevronRight className="flex-none w-4 h-4 mx-1.5 opacity-40" />
      <LocalizedLink to="/discovery" className="flex-none hover:text-stone-900 dark:hover:text-stone-100 transition-colors">
        {tDiscovery('sidebarTitle')}
      </LocalizedLink>
      <ChevronRight className="flex-none w-4 h-4 mx-1.5 opacity-40" />
      {shop.translation?.category && (
        <>
          <span className="flex-none text-stone-600 dark:text-stone-300">
            {shop.translation.category}
          </span>
          <ChevronRight className="flex-none w-4 h-4 mx-1.5 opacity-40" />
        </>
      )}
      <span className="flex-none text-stone-900 dark:text-stone-100 truncate max-w-[200px] lg:max-w-[300px]" title={shop.translation?.name || ''}>
        {shop.translation?.name}
      </span>
    </nav>
  )

  return (
    <div className="min-h-screen bg-surface pb-40 lg:pb-8">
      {/* Structural Breadcrumbs */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 pt-6 pb-4">
        {breadcrumbBlock}
      </div>

      <ShopHeroGallery 
        imageUrl={shop.imageUrl} 
        photos={shop.photos as string[] | null} 
        category={shop.translation?.category || ''} 
      />
      
      <div className="relative -mt-16 z-10 max-w-7xl mx-auto px-4 md:px-8">
        <ShopAwardsBadge awards={shop.translation?.awards as any} />
      </div>

      {hasRichContent ? (
        /* Two-column layout: enough content to fill the left column */
        <div className="max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-12 gap-y-8 lg:gap-x-12 px-4 md:px-8 pb-24 lg:pb-8">
          <div className="lg:col-span-7 xl:col-span-8 flex flex-col">
            {headerBlock}
            <ShopStory description={shop.translation?.description || null} />
            <ShopMenuSection menus={shop.translation?.menus as any} />
            <ShopHighlights highlights={shop.translation?.highlights as any} />
          </div>
          <div className="lg:col-span-5 xl:col-span-4 relative">
            <div className="sticky top-24 flex flex-col gap-6 max-h-[calc(100vh-6rem)] overflow-y-auto no-scrollbar pb-8">
              {scoreBlock}
              {actionBlock}
              {infoBlock}
            </div>
          </div>
        </div>
      ) : (
        /* Single-column layout: minimal content, stack everything vertically */
        <div className="max-w-3xl mx-auto w-full px-4 md:px-8 pb-24 lg:pb-8 flex flex-col gap-8">
          {headerBlock}
          {scoreBlock}
          {actionBlock}
          {infoBlock}
        </div>
      )}
    </div>
  )
}
